import { type PageLayoutProps, PageLayout } from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import { useLocation, useResolvedPath } from 'react-router-dom';

/** The sub-pages of a page, in the same tab bar as code.overheid.nl and a detail page. */
function SectionTabs({ tabs, label }: { tabs: NonNullable<PageLayoutProps['tabs']>; label?: string }) {
  const parentPath = useResolvedPath('.').pathname.replace(/\/$/, '');
  const { pathname } = useLocation();
  const links = tabs.map(tab => ({
    ...tab,
    href: tab.href.startsWith('/') ? tab.href : `${parentPath}/${tab.href}`.replace(/\/{2,}/g, '/'),
  }));
  const current = links
    .filter(tab => pathname === tab.href || pathname.startsWith(`${tab.href}/`))
    .sort((a, b) => b.href.length - a.href.length)[0];
  return (
    <div className="portal-section-tabs">
      <nldd-tab-bar navigation="" accessible-label={label ?? 'Onderdelen'}>
        {links.map(tab => (
          <nldd-tab-bar-item
            key={tab.id}
            text={tab.label}
            href={tab.href}
            current={tab.id === current?.id ? '' : undefined}
          />
        ))}
      </nldd-tab-bar>
    </div>
  );
}

const SECTION_INTROS: Record<string, string> = {
  'Aan de slag': 'Begin een nieuwe repository met de goede standaarden erin, of breng een bestaande op orde.',
};

/** A page with sub-pages opens with its name above the tabs, the same as on code.overheid.nl. */
function PortalPageLayout({ title, noHeader, tabs, children }: PageLayoutProps) {
  if (noHeader || !tabs?.length) {
    return <>{children}</>;
  }
  return (
    <>
      <div className="portal-section-head">
        <nldd-title
          size="1"
          heading-level="1"
          text={title}
          supporting-text={title ? SECTION_INTROS[title] : undefined}
        />
        <SectionTabs tabs={tabs} label={title} />
      </div>
      {children}
    </>
  );
}

export const pageLayout = SwappableComponentBlueprint.make({
  name: 'portal-page-layout',
  params: define => define({ component: PageLayout, loader: () => PortalPageLayout }),
});
